/**
 * REDEX Product Catalog
 * Central source of truth for all SKUs and pricing
 * 
 * TODO: Replace placeholder price IDs with actual Stripe price IDs
 */

export type SkuType = 'one_time' | 'subscription' | 'metered';

export interface Sku {
  id: string;
  name: string;
  type: SkuType;
  priceId: string; // Stripe price ID
  unitAmount: number; // In cents
  description: string;
  features?: string[];
  popular?: boolean;
}

// ============================================
// CORE PRODUCTS
// ============================================

export const SKUS: Record<string, Sku> = {
  // Core Site Build (one-time)
  core_build: {
    id: 'core_build',
    name: 'Core Site Build',
    type: 'one_time',
    priceId: 'price_1ScKRcAmx3PBCGtXBdZHs1Ml', unitAmount: 180000, // $1,800.00
    description: '8-12 page React site with Flask API, Dialpad integration, and full security stack',
    features: [
      '8-12 page React site with WCAG-aligned UX',
      'Flask API with Dialpad contact creation',
      'A2P verified telephony integration',
      'SSL/TLS/HSTS security',
      'Initial SEO meta & GA4/Plausible',
      'Hosting, backups, monitoring included',
    ],
  },

  // Deliverability Pack (add-on, one-time)
  deliverability: {
    id: 'deliverability',
    name: 'Deliverability Pack',
    type: 'one_time',
    priceId: 'price_1ScKRdAmx3PBCGtXX1qJ4NF8', unitAmount: 120000, // $1,200.00
    description: 'SPF/DKIM/DMARC + SES/SendGrid integration + warmup. Stops lead loss to spam. Implemented in 2 business days.',
    features: [
      'SPF/DKIM/DMARC configuration',
      'SES or SendGrid integration',
      'Email warmup automation',
      'Deliverability monitoring',
      'Spam score optimization',
    ],
    popular: true,
  },

  // Ops Enablement (add-on, one-time)
  ops_enablement: {
    id: 'ops_enablement',
    name: 'Ops Enablement (SOP)',
    type: 'one_time',
    priceId: 'price_1ScKRdAmx3PBCGtXflu0xDgv', unitAmount: 75000, // $750.00
    description: 'Signed SOP + dashboards + drill. Ensures daily lead triage (DialPad-first).',
    features: [
      'Complete Standard Operating Procedures',
      'Custom dashboards setup',
      'Team drill & training session',
      'Daily lead triage workflow',
      'DialPad-first call routing',
    ],
    popular: true,
  },

  // Audit & Evidence Vault (add-on, one-time)
  audit_vault: {
    id: 'audit_vault',
    name: 'Audit & Evidence Vault',
    type: 'one_time',
    priceId: 'price_1ScKReAmx3PBCGtXgEjasUqb', unitAmount: 50000, // $500.00
    description: 'Cutover report, logs, rollback plan—de-risks procurement and M&A diligence.',
    features: [
      'Comprehensive cutover report',
      'System logs & audit trail',
      'Rollback plan documentation',
      'M&A diligence package',
      'Compliance evidence export',
    ],
  },

  // Maintenance Standard (subscription, monthly)
  maintenance_std: {
    id: 'maintenance_std',
    name: 'Maintenance Plus',
    type: 'subscription',
    priceId: 'price_1ScKRfAmx3PBCGtXhOdnA7Xx', unitAmount: 39995, // $399.95/month
    description: 'Patches, backups, uptime checks, rolling updates (up to 10h).',
    features: [
      'Security patches & updates',
      'Daily automated backups',
      'Uptime monitoring (99.9% SLA)',
      'Up to 10 hours of updates/mo',
      'Priority support',
    ],
  },

  // Hourly Engineering (metered)
  hourly: {
    id: 'hourly',
    name: 'Engineering Hours',
    type: 'metered',
    priceId: 'price_1ScKRfAmx3PBCGtXkrtG3XOj', unitAmount: 18500, // $185.00/hour
    description: 'Additional engineering hours for custom development work',
    features: [
      'Custom feature development',
      'Integration work',
      'Performance optimization',
      'Architecture consulting',
    ],
  },

  // Retainer (prepaid 4-hour block)
  retainer_4h: {
    id: 'retainer_4h',
    name: '4-Hour Retainer Block',
    type: 'one_time',
    priceId: 'price_1ScKRgAmx3PBCGtXQLh3Ayv1', unitAmount: 74000, // $740.00 (4 x $185)
    description: 'Prepaid engineering hours at a discounted rate',
    features: [
      '4 hours of engineering time',
      '10% discount vs hourly rate',
      'Priority scheduling',
      '30-day validity',
    ],
  },
};

// ============================================
// BUNDLES
// ============================================

export interface Bundle {
  id: string;
  name: string;
  description: string;
  skuIds: string[];
  savings?: number; // Percentage discount
  popular?: boolean;
}

export const BUNDLES: Record<string, Bundle> = {
  core: {
    id: 'core',
    name: 'Core',
    description: 'Essential site build with full functionality',
    skuIds: ['core_build'],
  },

  core_plus: {
    id: 'core_plus',
    name: 'Core+',
    description: 'Core build plus email deliverability and operations enablement',
    skuIds: ['core_build', 'deliverability', 'ops_enablement'],
    savings: 10,
    popular: true,
  },

  pro: {
    id: 'pro',
    name: 'Pro',
    description: 'Complete package with all add-ons and ongoing maintenance',
    skuIds: ['core_build', 'deliverability', 'ops_enablement', 'audit_vault', 'maintenance_std'],
    savings: 15,
  },
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get SKU by ID
 */
export function getSku(skuId: string): Sku | undefined {
  return SKUS[skuId];
}

/**
 * Get bundle by ID
 */
export function getBundle(bundleId: string): Bundle | undefined {
  return BUNDLES[bundleId];
}

/**
 * Calculate bundle total
 */
export function calculateBundleTotal(bundleId: string): number {
  const bundle = getBundle(bundleId);
  if (!bundle) return 0;

  const total = bundle.skuIds.reduce((sum, skuId) => {
    const sku = getSku(skuId);
    return sum + (sku?.unitAmount || 0);
  }, 0);

  // Apply bundle discount if applicable
  if (bundle.savings) {
    return Math.round(total * (1 - bundle.savings / 100));
  }

  return total;
}

/**
 * Get bundle SKUs
 */
export function getBundleSkus(bundleId: string): Sku[] {
  const bundle = getBundle(bundleId);
  if (!bundle) return [];

  return bundle.skuIds
    .map(skuId => getSku(skuId))
    .filter((sku): sku is Sku => sku !== undefined);
}

/**
 * Format price for display
 */
export function formatPrice(amountInCents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amountInCents / 100);
}

/**
 * Get all add-on SKUs (excludes core_build)
 */
export function getAddOns(): Sku[] {
  return Object.values(SKUS).filter(
    sku => sku.id !== 'core_build' && sku.type === 'one_time'
  );
}

/**
 * Get subscription SKUs
 */
export function getSubscriptions(): Sku[] {
  return Object.values(SKUS).filter(sku => sku.type === 'subscription');
}

/**
 * Separate cart items by type (for Stripe checkout handling)
 */
export function separateByType(skuIds: string[]): {
  oneTime: string[];
  subscription: string[];
  metered: string[];
} {
  const oneTime: string[] = [];
  const subscription: string[] = [];
  const metered: string[] = [];

  skuIds.forEach(skuId => {
    const sku = getSku(skuId);
    if (!sku) return;

    switch (sku.type) {
      case 'one_time':
        oneTime.push(skuId);
        break;
      case 'subscription':
        subscription.push(skuId);
        break;
      case 'metered':
        metered.push(skuId);
        break;
    }
  });

  return { oneTime, subscription, metered };
}
